import {Component, OnInit, inject, ChangeDetectionStrategy} from '@angular/core';
import {Configuration, WarehouseReleaseTableConfig, WarehouseReleaseTableConfigData} from './configuration.model';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {WeatherConfig, WeatherWindUnit} from './weather-config.model';
import {switchMap, tap} from 'rxjs/operators';
import {MasterDataService} from '../master-data/master-data-service';
import {Language, Status} from '../master-data/master-data.model';
import {ConfigurationService} from './configuration.service';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {LanguageService} from '../shared/language.service';
import {TranslationSettingsService} from '../shared/translation-dialog/translation-settings.service';

/** Caution/Stop pairs of the weather limits must make sense together (checked only while weather is on). */
const weatherLimitsValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const v = (group as FormGroup).getRawValue();
  if (!v.enabled) {
    return null;
  }
  const errors: ValidationErrors = {};
  if (!(v.gustWarn > 0) || !(v.gustStop > v.gustWarn)) {
    errors['gust'] = true;
  }
  if (!(v.rainWarn > 0) || !(v.rainStop > v.rainWarn)) {
    errors['rain'] = true;
  }
  if (!(v.tempLowStop < v.tempLowWarn) || !(v.tempLowWarn < v.tempHighWarn)) {
    errors['low'] = true;
  }
  if (!(v.tempHighWarn < v.tempHighStop)) {
    errors['high'] = true;
  }
  return Object.keys(errors).length ? errors : null;
};

@Component({
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ConfigurationComponent implements OnInit {
  private configService = inject(ConfigurationService);
  private masterDataService = inject(MasterDataService);
  private formBuilder = inject(FormBuilder);
  private languageService = inject(LanguageService);
  private translationSettingsService = inject(TranslationSettingsService);

  configLoaded: boolean | null = null;

  config: Configuration;
  configUuid = null;
  noConfigDefined = false;

  statuses: Status[];
  allLanguages: Language[] = [];

  displayedColumns = ['name', 'sort', 'width', 'unit', 'visible'];


  configForm = this.formBuilder.group({
    warning: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(2),
    ]),
    alert: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    orderNumberDateFrom: new FormControl<string>('due_date', [
      Validators.required,
    ]),
    menuHideRequests: [true],
    menuHideAgreements: [true],
    menuHideWorkTime: [false],
    menuHideWarehouses: [false],
    menuHideProducts: [false],
    reportsEnabled: [false],
    allowOrderWoDevices: [false],
    dateFormat: ['', Validators.required],
    timeFormat: ['', Validators.required],
    dateTimeFormat: ['', Validators.required],

    enableUserSchedule: new FormControl<boolean | null>(null, Validators.required),
    teamLeadOn: new FormControl<boolean | null>(null, Validators.required),
    statusChangeWorkOff: new FormControl<boolean | null>(
      null,
      Validators.required
    ),
    noteAddWorkOff: new FormControl<boolean | null>(null, Validators.required),
    photoAddWorkOff: new FormControl<boolean | null>(null, Validators.required),
    allowPhotoFromGallery: new FormControl<boolean | null>(
      null,
      Validators.required
    ),
    allowAddingPdfToOrder: new FormControl<boolean | null>(
      null,
      Validators.required
    ),
    formAllowSaveAsDraft: new FormControl<boolean | null>(
      null,
      Validators.required
    ),
    formAllowCopyFromPDF: new FormControl<boolean | null>(
      null,
      Validators.required
    ),
    formFieldFontFactor: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0.1),
    ]),
    formTableFontFactor: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0.1),
    ]),
    formTableMaxRow: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
    formMultilanguageEnable: new FormControl<boolean>(false, { nonNullable: true }),
    formDefaultLanguage: new FormControl<string>('', { nonNullable: true }),
    formEmailAddress: [''],
    formCopyEmailAddress: [''],
    formEmailTemplateId: [''],
    warehouseReleaseTableConfig: new FormGroup({
      itemKey: new FormGroup({
        key: new FormControl('itemKey', Validators.required),
        formFieldType: new FormControl(3, Validators.required),
        name: new FormControl('Index', Validators.required),
        sort: new FormControl(2, Validators.required),
        width: new FormControl(2, Validators.required),
        visible: new FormControl(true, Validators.required),
      }),
      quantity: new FormGroup({
        key: new FormControl('quantity', Validators.required),
        formFieldType: new FormControl(5, Validators.required),
        name: new FormControl('Ilość', Validators.required),
        sort: new FormControl(3, Validators.required),
        width: new FormControl(1, Validators.required),
        visible: new FormControl(true, Validators.required),
      }),
      name: new FormGroup({
        key: new FormControl('name', Validators.required),
        formFieldType: new FormControl(3, Validators.required),
        name: new FormControl('Nazwa', Validators.required),
        sort: new FormControl(1, Validators.required),
        width: new FormControl(5, Validators.required),
        visible: new FormControl(true, Validators.required),
      }),
      unit: new FormGroup({
        key: new FormControl('unit', Validators.required),
        formFieldType: new FormControl(3, Validators.required),
        name: new FormControl('J.m.', Validators.required),
        sort: new FormControl(4, Validators.required),
        width: new FormControl(1, Validators.required),
        visible: new FormControl(true, Validators.required),
      }),
      description: new FormGroup({
        key: new FormControl('description', Validators.required),
        formFieldType: new FormControl(3, Validators.required),
        name: new FormControl('Opis', Validators.required),
        sort: new FormControl(5, Validators.required),
        width: new FormControl(-1, Validators.required),
        visible: new FormControl(false, Validators.required),
      }),
    }),
    warehouseEnableStockOrder: new FormControl<boolean | null>(
      null,
      Validators.required
    ),
    warehouseAllowNegativeStock: new FormControl<boolean | null>(null),
    warehouseNotShowZeroStock: [true],
    warehouseAllowAdditionalSrvm: new FormControl<boolean | null>(null),
    warehouseAllowedStatuses: [],
    branchesEnable: [false],
    branchesChangeByBranchAdmin: [false],
    branchesRemoveTeamFromOrder: [true],
    departmentsEnable: [false],
    sequentialActionsEnable: [false],
    customerAllowDuplicatedVATcode: [false],
    enableSupervisorRole: [false],
    trackingEnable: new FormControl<boolean | null>(false, Validators.required),
    trackingInBackground:new FormControl<boolean | null>(false, Validators.required),
    trackingHistoryDays:new FormControl<number | null>(5, [
      Validators.required,
      Validators.min(2),
      Validators.max(45),
    ]),
    trackingCalculateDistance:new FormControl<boolean | null>(false, Validators.required),
    trackingDistanceHistoryDays: new FormControl<number | null>(5, [
      Validators.required,
      Validators.min(5),
      Validators.max(90),
    ]),
    securityEnableMFA:new FormControl<boolean | null>(false),
    securityMFASMS:new FormControl<boolean | null>(false),
    securityMFAAuthApp:new FormControl<boolean | null>(false),
    securityMFAPhoneLocalBiometric:new FormControl<boolean | null>(false),
    securityLoginLocalBiometric:new FormControl<boolean | null>(false),
    languages: new FormControl<string[]>([]),
    // Weather forecast on Map – planning; gusts are entered in the selected wind unit, stored in m/s.
    weather: new FormGroup({
      enabled: new FormControl<boolean>(false, { nonNullable: true }),
      forecastDays: new FormControl<number | null>(5, [Validators.required, Validators.min(1), Validators.max(7)]),
      windUnit: new FormControl<WeatherWindUnit>('ms', { nonNullable: true }),
      gustWarn: new FormControl<number | null>(10, Validators.required),
      gustStop: new FormControl<number | null>(15, Validators.required),
      rainWarn: new FormControl<number | null>(1, Validators.required),
      rainStop: new FormControl<number | null>(5, Validators.required),
      tempLowWarn: new FormControl<number | null>(0, Validators.required),
      tempLowStop: new FormControl<number | null>(-5, Validators.required),
      tempHighWarn: new FormControl<number | null>(30, Validators.required),
      tempHighStop: new FormControl<number | null>(35, Validators.required),
      stormStop: new FormControl<boolean>(true, { nonNullable: true }),
      stormProb: new FormControl<number | null>(40, [Validators.required, Validators.min(1), Validators.max(100)]),
    }, { validators: weatherLimitsValidator }),
  });

  /** Unit the gust inputs currently show. */
  private weatherUnit: WeatherWindUnit = 'ms';

  get weatherUnitLabel(): string {
    return this.weatherUnit === 'kmh' ? 'km/h' : 'm/s';
  }

  ngOnInit(): void {
    this.languageService.loadLanguages().subscribe((languages) => {
      this.allLanguages = languages;
      this.syncTranslationSettings();
      this.ensureFormDefaultLanguage();
    });
    this.configForm.controls.formMultilanguageEnable.valueChanges.subscribe(
      () => this.ensureFormDefaultLanguage()
    );
    this.loadConfig();

    this.toggleTrackingDependentControls(this.configForm.controls.trackingEnable.value);
    this.configForm.controls.trackingEnable.valueChanges.subscribe((enabled) => {
      this.toggleTrackingDependentControls(enabled);
    });

    // MFA wiring
    this.toggleMFADependentControls(
      this.configForm.controls.securityEnableMFA.value
    );
    this.configForm.controls.securityEnableMFA.valueChanges.subscribe(
      (enabled) => {
        this.toggleMFADependentControls(enabled);
      }
    );

    // Weather wiring
    const weather = this.configForm.controls.weather.controls;
    this.toggleWeatherDependentControls(weather.enabled.value);
    weather.enabled.valueChanges.subscribe((enabled) => this.toggleWeatherDependentControls(enabled));
    weather.windUnit.valueChanges.subscribe((unit) => this.onWeatherUnitChange(unit));
  }

  private toggleWeatherDependentControls(enabled: boolean | null): void {
    const controls = this.configForm.controls.weather.controls;
    for (const [name, control] of Object.entries(controls)) {
      if (name === 'enabled') {
        continue;
      }
      if (enabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }
    this.configForm.controls.weather.updateValueAndValidity({ emitEvent: false });
  }

  /** Converts the gust limits shown in the inputs when the wind unit changes. */
  private onWeatherUnitChange(unit: WeatherWindUnit | null): void {
    const next: WeatherWindUnit = unit === 'kmh' ? 'kmh' : 'ms';
    if (next === this.weatherUnit) {
      return;
    }
    const factor = (this.weatherUnit === 'kmh' ? 1 / 3.6 : 1) * (next === 'kmh' ? 3.6 : 1);
    const controls = this.configForm.controls.weather.controls;
    for (const control of [controls.gustWarn, controls.gustStop]) {
      if (control.value !== null && control.value !== undefined) {
        control.setValue(Math.round(control.value * factor * 10) / 10, { emitEvent: false });
      }
    }
    this.weatherUnit = next;
    this.configForm.controls.weather.updateValueAndValidity({ emitEvent: false });
  }

  private setWeatherToForm(weather: WeatherConfig): void {
    const k = weather.windUnit === 'kmh' ? 3.6 : 1;
    this.weatherUnit = weather.windUnit;
    this.configForm.controls.weather.setValue({
      enabled: weather.enabled,
      forecastDays: weather.forecastDays,
      windUnit: weather.windUnit,
      gustWarn: Math.round(weather.gustWarn * k * 10) / 10,
      gustStop: Math.round(weather.gustStop * k * 10) / 10,
      rainWarn: weather.rainWarn,
      rainStop: weather.rainStop,
      tempLowWarn: weather.tempLowWarn,
      tempLowStop: weather.tempLowStop,
      tempHighWarn: weather.tempHighWarn,
      tempHighStop: weather.tempHighStop,
      stormStop: weather.stormStop,
      stormProb: weather.stormProb,
    }, { emitEvent: false });
    this.toggleWeatherDependentControls(weather.enabled);
  }

  private getWeatherFromForm(): WeatherConfig {
    const v = this.configForm.controls.weather.getRawValue();
    const toMs = v.windUnit === 'kmh' ? 1 / 3.6 : 1;
    const weather = new WeatherConfig();
    weather.enabled = !!v.enabled;
    weather.forecastDays = v.forecastDays ?? weather.forecastDays;
    weather.windUnit = v.windUnit === 'kmh' ? 'kmh' : 'ms';
    weather.gustWarn = (v.gustWarn ?? weather.gustWarn) * toMs;
    weather.gustStop = (v.gustStop ?? weather.gustStop) * toMs;
    weather.rainWarn = v.rainWarn ?? weather.rainWarn;
    weather.rainStop = v.rainStop ?? weather.rainStop;
    weather.tempLowWarn = v.tempLowWarn ?? weather.tempLowWarn;
    weather.tempLowStop = v.tempLowStop ?? weather.tempLowStop;
    weather.tempHighWarn = v.tempHighWarn ?? weather.tempHighWarn;
    weather.tempHighStop = v.tempHighStop ?? weather.tempHighStop;
    weather.stormStop = !!v.stormStop;
    weather.stormProb = v.stormProb ?? weather.stormProb;
    return weather;
  }

  private toggleTrackingDependentControls(enabled: boolean | null): void {
    const trackingInBackground = this.configForm.controls.trackingInBackground;
    const trackingHistoryDays = this.configForm.controls.trackingHistoryDays;
    const trackingCalculateDistance = this.configForm.controls.trackingCalculateDistance;
    const trackingDistanceHistoryDays = this.configForm.controls.trackingDistanceHistoryDays;

    if (enabled) {
      trackingInBackground.enable({ emitEvent: false });
      trackingHistoryDays.enable({ emitEvent: false });
      trackingCalculateDistance.enable({ emitEvent: false });
      trackingDistanceHistoryDays.enable({ emitEvent: false });
    } else {
      trackingInBackground.setValue(false, { emitEvent: false });
      trackingCalculateDistance.setValue(false, { emitEvent: false });

      trackingInBackground.disable({ emitEvent: false });
      trackingHistoryDays.disable({ emitEvent: false });
      trackingCalculateDistance.disable({ emitEvent: false });
      trackingDistanceHistoryDays.disable({ emitEvent: false });
    }
  }

  private toggleMFADependentControls(enabled: boolean | null): void {
    const securityMFASMS = this.configForm.controls.securityMFASMS;
    const securityMFAAuthApp = this.configForm.controls.securityMFAAuthApp;
    const securityMFAPhoneLocalBiometric =
      this.configForm.controls.securityMFAPhoneLocalBiometric;

    if (enabled) {
      securityMFASMS.enable({ emitEvent: false });
      securityMFAAuthApp.enable({ emitEvent: false });
      securityMFAPhoneLocalBiometric.enable({ emitEvent: false });
    } else {
      // clear / reset dependent options when MFA is disabled
      securityMFASMS.setValue(false, { emitEvent: false });
      securityMFAAuthApp.setValue(false, { emitEvent: false });
      securityMFAPhoneLocalBiometric.setValue(false, { emitEvent: false });

      securityMFASMS.disable({ emitEvent: false });
      securityMFAAuthApp.disable({ emitEvent: false });
      securityMFAPhoneLocalBiometric.disable({ emitEvent: false });
    }
  }

  loadStatuses() {
    return this.masterDataService
      .getStatuses()
      .pipe(
        tap(
          (statuses) =>
            (this.statuses = statuses
              .filter(
                (s) =>
                  s.metaId.toString().substring(0, 1) === '2' ||
                  s.metaId.toString().substring(0, 1) === '3'
              )
              .sort((a, b) =>
                a.metaId > b.metaId ? 1 : b.metaId > a.metaId ? -1 : 0
              ))
        )
      );
  }

  public compareStatuses(c1: Status, c2: Status): boolean {
    return c1 && c2 && c1.uuid === c2.uuid;
  }

  /**
   * Maps WarehouseReleaseTableConfigData to FormGroup value structure
   * Handles exact nested FormGroup structure in configForm.controls.warehouseReleaseTableConfig
   */
  setWarehouseReleaseTableConfigToForm(
    formGroup: FormGroup,
    configData: WarehouseReleaseTableConfigData | null
  ): void {
    if (!configData?.warehouseReleaseTableConfig) {
      formGroup.reset();  // Clear if no data
      return;
    }

    const config = configData.warehouseReleaseTableConfig;

    formGroup.setValue({
      itemKey: {
        key: config.itemKey.key,
        formFieldType: config.itemKey.formFieldType,
        name: config.itemKey.name,
        sort: config.itemKey.sort,
        width: config.itemKey.width,
        visible: config.itemKey.visible
      },
      quantity: {
        key: config.quantity.key,
        formFieldType: config.quantity.formFieldType,
        name: config.quantity.name,
        sort: config.quantity.sort,
        width: config.quantity.width,
        visible: config.quantity.visible
      },
      name: {
        key: config.name.key,
        formFieldType: config.name.formFieldType,
        name: config.name.name,
        sort: config.name.sort,
        width: config.name.width,
        visible: config.name.visible
      },
      unit: {
        key: config.unit.key,
        formFieldType: config.unit.formFieldType,
        name: config.unit.name,
        sort: config.unit.sort,
        width: config.unit.width,
        visible: config.unit.visible
      },
      description: {
        key: config.description.key,
        formFieldType: config.description.formFieldType,
        name: config.description.name,
        sort: config.description.sort,
        width: config.description.width,
        visible: config.description.visible
      }
    });

    // Apply readonly logic based on unit.visible value
    const unitWidthControl = formGroup.get('unit.width');
    if (config.unit.visible === false) {
      // If visible = false: set width to -1 and disable
      unitWidthControl?.setValue(-1, {emitEvent: false});
      unitWidthControl?.disable({emitEvent: false});
    } else {
      // If visible = true: enable width field
      unitWidthControl?.enable({emitEvent: false});
    }
    // Apply readonly logic based on description.visible value
    const descriptionWidthControl = formGroup.get('description.width');
    if (config.description.visible === false) {
      // If visible = false: set width to -1 and disable
      descriptionWidthControl?.setValue(-1, {emitEvent: false});
      descriptionWidthControl?.disable({emitEvent: false});
    } else {
      // If visible = true: enable width field
      descriptionWidthControl?.enable({emitEvent: false});
    }

  }

  /**
   * Reads FormGroup value and creates WarehouseReleaseTableConfigData instance
   * Maps exact nested FormGroup → class structure
   */
  getWarehouseReleaseTableConfigFromForm(formGroup: FormGroup): WarehouseReleaseTableConfigData | null {
    const formValue = formGroup.getRawValue() as WarehouseReleaseTableConfig;

    if (!formValue || Object.keys(formValue).length === 0) {
      return null;
    }

    // Create raw config from form values
    const config: WarehouseReleaseTableConfig = {
      itemKey: {
        key: formValue.itemKey.key,
        formFieldType: formValue.itemKey.formFieldType,
        name: formValue.itemKey.name,
        sort: formValue.itemKey.sort,
        width: formValue.itemKey.width,
        visible: formValue.itemKey.visible
      },
      quantity: {
        key: formValue.quantity.key,
        formFieldType: formValue.quantity.formFieldType,
        name: formValue.quantity.name,
        sort: formValue.quantity.sort,
        width: formValue.quantity.width,
        visible: formValue.quantity.visible
      },
      name: {
        key: formValue.name.key,
        formFieldType: formValue.name.formFieldType,
        name: formValue.name.name,
        sort: formValue.name.sort,
        width: formValue.name.width,
        visible: formValue.name.visible
      },
      unit: {
        key: formValue.unit.key,
        formFieldType: formValue.unit.formFieldType,
        name: formValue.unit.name,
        sort: formValue.unit.sort,
        width: formValue.unit.width,
        visible: formValue.unit.visible
      },
      description: {
        key: formValue.description.key,
        formFieldType: formValue.description.formFieldType,
        name: formValue.description.name,
        sort: formValue.description.sort,
        width: formValue.description.width,
        visible: formValue.description.visible
      }
    };

    // Instantiate class with constructor mapping
    return new WarehouseReleaseTableConfigData(config);
  }


  patchValues() {
    this.configForm.controls.warning.setValue(this.config.warning);
    this.configForm.controls.alert.setValue(this.config.alert);
    this.configForm.controls.orderNumberDateFrom.setValue(this.config.orderNumberDateFrom);
    this.configForm.controls.menuHideRequests.setValue(
      this.config.menuHideRequests
    );
    this.configForm.controls.menuHideAgreements.setValue(
      this.config.menuHideAgreements
    );
    this.configForm.controls.menuHideWorkTime.setValue(
      this.config.menuHideWorkTime
    );
    this.configForm.controls.menuHideWarehouses.setValue(
      this.config.menuHideWarehouses
    );
    this.configForm.controls.menuHideProducts.setValue(
      this.config.menuHideProducts
    );
    this.configForm.controls.reportsEnabled.setValue(
      this.config.reportsEnabled
    );
    this.configForm.controls.allowOrderWoDevices.setValue(
      this.config.allowOrderWoDevices
    );
    this.configForm.controls.dateFormat.setValue(this.config.dateFormat);
    this.configForm.controls.timeFormat.setValue(this.config.timeFormat);
    this.configForm.controls.dateTimeFormat.setValue(
      this.config.dateTimeFormat
    );

    this.configForm.controls.enableUserSchedule.setValue(this.config.enableUserSchedule);
    this.configForm.controls.teamLeadOn.setValue(this.config.teamLeadOn);
    this.configForm.controls.statusChangeWorkOff.setValue(
      this.config.statusChangeWorkOff
    );
    this.configForm.controls.noteAddWorkOff.setValue(
      this.config.noteAddWorkOff
    );
    this.configForm.controls.photoAddWorkOff.setValue(
      this.config.photoAddWorkOff
    );
    this.configForm.controls.allowPhotoFromGallery.setValue(
      this.config.allowPhotoFromGallery
    );
    this.configForm.controls.allowAddingPdfToOrder.setValue(
      this.config.allowAddingPdfToOrder
    );
    this.configForm.controls.formAllowSaveAsDraft.setValue(
      this.config.formAllowSaveAsDraft
    );
    this.configForm.controls.formAllowCopyFromPDF.setValue(
      this.config.formAllowCopyFromPDF
    );
    this.configForm.controls.formFieldFontFactor.setValue(
      this.config.formFieldFontFactor
    );
    this.configForm.controls.formTableFontFactor.setValue(
      this.config.formTableFontFactor
    );
    this.configForm.controls.formTableMaxRow.setValue(
      this.config.formTableMaxRow
    );
    this.configForm.controls.formMultilanguageEnable.setValue(
      this.config.formMultilanguageEnable
    );
    this.configForm.controls.formDefaultLanguage.setValue(
      this.config.formDefaultLanguage
    );
    this.ensureFormDefaultLanguage();
    this.configForm.controls.formEmailAddress.setValue(
      this.config.formEmailAddress
    );
    this.configForm.controls.formCopyEmailAddress.setValue(
      this.config.formCopyEmailAddress
    );
    this.configForm.controls.formEmailTemplateId.setValue(
      this.config.formEmailTemplateId
    );
    this.setWarehouseReleaseTableConfigToForm(
      this.configForm.controls.warehouseReleaseTableConfig as FormGroup,
      this.config.warehouseReleaseTableConfigData  // ✅ Maps to exact FormGroup structure
    );
    this.configForm.controls.warehouseEnableStockOrder.setValue(
      this.config.warehouseEnableStockOrder
    );
    this.configForm.controls.warehouseAllowNegativeStock.setValue(
      this.config.warehouseAllowNegativeStock
    );
    this.configForm.controls.warehouseNotShowZeroStock.setValue(
      this.config.warehouseNotShopZeroStock
    );
    this.configForm.controls.warehouseAllowAdditionalSrvm.setValue(
      this.config.warehouseAllowAdditionalSrvm
    );
    const selectedStatuses = this.statuses?.filter((s) =>
      this.config.warehouseAllowedStatuses?.includes(s.uuid)
    );
    this.configForm.controls.warehouseAllowedStatuses.patchValue([
      ...selectedStatuses,
    ]);
    this.configForm.controls.branchesEnable.setValue(
      this.config.branchesEnable
    );
    this.configForm.controls.branchesChangeByBranchAdmin.setValue(
      this.config.branchesBranchChangeByBranchAdmin
    );
    this.configForm.controls.branchesRemoveTeamFromOrder.setValue(
      this.config.branchesRemoveTeamFromOrder
    );
    this.configForm.controls.departmentsEnable.setValue(
      this.config.departmentsEnable
    );
    this.configForm.controls.sequentialActionsEnable.setValue(
      this.config.sequentialActionsEnable
    );
    this.configForm.controls.customerAllowDuplicatedVATcode.setValue(
      this.config.customerAllowDuplicatedVATcode
    );
    this.configForm.controls.enableSupervisorRole.setValue(
      this.config.enableSupervisorRole
    );
    this.configForm.controls.trackingEnable.setValue(this.config.trackingEnable);
    this.configForm.controls.trackingInBackground.setValue(this.config.trackingInBackground);
    this.configForm.controls.trackingHistoryDays.setValue(this.config.trackingHistoryDays);
    this.configForm.controls.trackingCalculateDistance.setValue(this.config.trackingCalculateDistance);
    this.configForm.controls.trackingDistanceHistoryDays.setValue(this.config.trackingDistanceHistoryDays);
    this.configForm.controls.securityEnableMFA.setValue(this.config.securityEnableMFA);
    this.configForm.controls.securityMFASMS.setValue(this.config.securityMFASMS);
    this.configForm.controls.securityMFAAuthApp.setValue(this.config.securityMFAAuthApp);
    this.configForm.controls.securityMFAPhoneLocalBiometric.setValue(this.config.securityMFAPhoneLocalBiometric);
    this.configForm.controls.securityLoginLocalBiometric.setValue(this.config.securityLoginLocalBiometric);
    this.configForm.controls.languages.setValue(this.config.languages ?? []);
    this.setWeatherToForm(this.config.weather);
    this.syncTranslationSettings();

  }

  readValues() {
    this.config.warning = this.configForm.controls.warning.value;
    this.config.alert = this.configForm.controls.alert.value;
    this.config.orderNumberDateFrom = this.configForm.controls.orderNumberDateFrom.value;
    this.config.menuHideRequests =
      this.configForm.controls.menuHideRequests.value;
    this.config.menuHideAgreements =
      this.configForm.controls.menuHideAgreements.value;
    this.config.menuHideWorkTime =
      this.configForm.controls.menuHideWorkTime.value;
    this.config.menuHideWarehouses =
      this.configForm.controls.menuHideWarehouses.value;
    this.config.menuHideProducts =
      this.configForm.controls.menuHideProducts.value;
    this.config.reportsEnabled =
      this.configForm.controls.reportsEnabled.value;
    this.config.allowOrderWoDevices =
      this.configForm.controls.allowOrderWoDevices.value;
    this.config.dateFormat = this.configForm.controls.dateFormat.value;
    this.config.timeFormat = this.configForm.controls.timeFormat.value;
    this.config.dateTimeFormat = this.configForm.controls.dateTimeFormat.value;
    this.config.enableUserSchedule = this.configForm.controls.enableUserSchedule.value;
    this.config.teamLeadOn = this.configForm.controls.teamLeadOn.value;
    this.config.statusChangeWorkOff =
      this.configForm.controls.statusChangeWorkOff.value;
    this.config.noteAddWorkOff = this.configForm.controls.noteAddWorkOff.value;
    this.config.photoAddWorkOff =
      this.configForm.controls.photoAddWorkOff.value;
    this.config.allowPhotoFromGallery =
      this.configForm.controls.allowPhotoFromGallery.value;
    this.config.allowAddingPdfToOrder =
      this.configForm.controls.allowAddingPdfToOrder.value;
    this.config.formAllowSaveAsDraft =
      this.configForm.controls.formAllowSaveAsDraft.value;
    this.config.formAllowCopyFromPDF =
      this.configForm.controls.formAllowCopyFromPDF.value;
    this.config.formFieldFontFactor =
      this.configForm.controls.formFieldFontFactor.value;
    this.config.formTableFontFactor =
      this.configForm.controls.formTableFontFactor.value;
    this.config.formTableMaxRow =
      this.configForm.controls.formTableMaxRow.value;
    this.config.formMultilanguageEnable =
      this.configForm.controls.formMultilanguageEnable.value;
    this.config.formDefaultLanguage =
      this.configForm.controls.formDefaultLanguage.value;
    this.config.formEmailAddress =
      this.configForm.controls.formEmailAddress.value;
    this.config.formCopyEmailAddress =
      this.configForm.controls.formCopyEmailAddress.value;
    this.config.formEmailTemplateId =
      this.configForm.controls.formEmailTemplateId.value;
    this.config.warehouseReleaseTableConfigData =
      this.getWarehouseReleaseTableConfigFromForm(
        this.configForm.controls.warehouseReleaseTableConfig as FormGroup
      );
    this.config.warehouseEnableStockOrder =
      this.configForm.controls.warehouseEnableStockOrder.value;
    this.config.warehouseAllowNegativeStock =
      this.configForm.controls.warehouseAllowNegativeStock.value;
    this.config.warehouseNotShopZeroStock =
      this.configForm.controls.warehouseNotShowZeroStock.value;
    this.config.warehouseAllowAdditionalSrvm =
      this.configForm.controls.warehouseAllowAdditionalSrvm.value;
    this.config.warehouseAllowedStatuses =
      this.configForm.controls.warehouseAllowedStatuses.value.map(
        (s) => s.uuid
      );
    this.config.branchesEnable = this.configForm.controls.branchesEnable.value;
    this.config.branchesBranchChangeByBranchAdmin =
      this.configForm.controls.branchesChangeByBranchAdmin.value;
    this.config.branchesRemoveTeamFromOrder =
      this.configForm.controls.branchesRemoveTeamFromOrder.value;
    this.config.departmentsEnable =
      this.configForm.controls.departmentsEnable.value;
    this.config.sequentialActionsEnable =
      this.configForm.controls.sequentialActionsEnable.value;
    this.config.customerAllowDuplicatedVATcode =
      this.configForm.controls.customerAllowDuplicatedVATcode.value;
    this.config.enableSupervisorRole =
      this.configForm.controls.enableSupervisorRole.value;
    this.config.trackingEnable = this.configForm.controls.trackingEnable.value;
    this.config.trackingInBackground = this.configForm.controls.trackingInBackground.value;
    this.config.trackingHistoryDays = this.configForm.controls.trackingHistoryDays.value;
    this.config.trackingCalculateDistance = this.configForm.controls.trackingCalculateDistance.value;
    this.config.trackingDistanceHistoryDays = this.configForm.controls.trackingDistanceHistoryDays.value;
    this.config.securityEnableMFA = this.configForm.controls.securityEnableMFA.value;
    this.config.securityMFASMS = this.configForm.controls.securityMFASMS.value;
    this.config.securityMFAAuthApp = this.configForm.controls.securityMFAAuthApp.value;
    this.config.securityMFAPhoneLocalBiometric = this.configForm.controls.securityMFAPhoneLocalBiometric.value;
    this.config.securityLoginLocalBiometric = this.configForm.controls.securityLoginLocalBiometric.value;
    this.config.languages = this.configForm.controls.languages.value ?? [];
    this.config.weather = this.getWeatherFromForm();
  }

  loadConfig() {
    this.loadStatuses()
      .pipe(
        tap((_) => this.configService.loadConfig()),
        switchMap((_) => this.configService.config$)
      )
      .subscribe({
        next: (response) => {
          this.configLoaded = true;
          if (response) {
            this.noConfigDefined = false;
            this.config = new Configuration();
            this.config.setConfig(new Map(Object.entries(response.configMap)));
            this.configUuid = response.uuid;
            setTimeout(() => {
              this.patchValues();
            }, 1);
          } else {
            this.noConfigDefined = true;
          }
        },
        error: (error) => {
          console.log('Http error');
          this.configLoaded = false;
          this.configUuid = null;
        },
      });
  }

  clearRefresh() {
    this.configLoaded = null;
    this.config = null;
    this.configForm.reset();
    this.configUuid = null;
  }

  createConfig() {
    this.configLoaded = true;
    this.noConfigDefined = false;
    this.config = new Configuration();
    this.config.setDefault();
    this.configUuid = null;
    this.patchValues();
  }

  saveConfig() {
    this.readValues();
    const map = this.config.toMap(this.config);
    this.configService.saveConfiguration(map, this.configUuid);
    this.syncTranslationSettings();
    this.configForm.reset();
  }

  /**
   * Keeps TranslationSettingsService (used app-wide to decide which language
   * tabs the "translate" dialogs show) in sync with the persisted tenant
   * language selection, so a change here is reflected immediately without
   * requiring a full app reload.
   */
  /**
   * Multi-language forms always get an explicit default form language:
   * preselect the platform default (languages.as_default) when none is set.
   */
  private ensureFormDefaultLanguage(): void {
    const control = this.configForm.controls.formDefaultLanguage;
    if (!this.configForm.controls.formMultilanguageEnable.value || control.value) {
      return;
    }
    const enabled = this.allLanguages.filter((l) => l.enabled);
    const language = enabled.find((l) => l.asDefault) ?? enabled[0];
    if (language) {
      control.setValue(language.locale);
      control.markAsDirty();
    }
  }

  private syncTranslationSettings(): void {
    if (this.config && this.allLanguages.length > 0) {
      this.translationSettingsService.loadFromLocales(this.config.languages ?? [], this.allLanguages);
    }
  }

  protected onUnitVisibilityChange(event: MatCheckboxChange): void {
    const unitGroup = this.configForm.get('warehouseReleaseTableConfig.unit') as FormGroup;
    const widthControl = unitGroup?.get('width');

    if (event.checked) {
      // Checkbox is TRUE (visible) - enable width field
      widthControl?.setValue(1);
      widthControl?.enable();
    } else {
      // Checkbox is FALSE (not visible) - disable and set to -1
      widthControl?.setValue(-1);
      widthControl?.disable();
    }
  }

  protected onDescriptionVisibilityChange(event: MatCheckboxChange): void {
    const descriptionGroup = this.configForm.get('warehouseReleaseTableConfig.description') as FormGroup;
    const widthControl = descriptionGroup?.get('width');

    if (event.checked) {
      // Checkbox is TRUE (visible) - enable width field
      widthControl?.setValue(1);
      widthControl?.enable();
    } else {
      // Checkbox is FALSE (not visible) - disable and set to -1
      widthControl?.setValue(-1);
      widthControl?.disable();
    }
  }

}
